import { useEffect } from "react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import type { Environment } from "@akina/config/environmentService";
import * as Sentry from "@sentry/react";
import { BadRequestException } from "./BadRequestException";
import { Exception } from "./Exception";
import { ForbiddenException } from "./ForbiddenException";
import { InternalServerErrorException } from "./InternalServerErrorException";
import { InvalidComponentBlockException } from "./InvalidComponentBlockException";
import { NotAcceptableException } from "./NotAcceptableException";
import { NotFoundErrorException } from "./NotFoundException";
import { UnauthorizedException } from "./UnauthorizedException";
import { UnprocessableErrorException } from "./UnprocessableErrorException";
import { WebsocketErrorException } from "./WebsocketErrorException";

export type ExceptionsStatusCodes = 400 | 401 | 403 | 404 | 406 | 422 | 500;

export const Exceptions = {
  // status codes
  400: BadRequestException,
  401: UnauthorizedException,
  403: ForbiddenException,
  404: NotFoundErrorException,
  406: NotAcceptableException,
  422: UnprocessableErrorException,
  500: InternalServerErrorException,
  // custom
  WebsocketRecoveryAttemptsExceededThreshold: WebsocketErrorException,
  Exception: Exception,
  InvalidComponentBlock: InvalidComponentBlockException,
} as const;

const ENV_TO_TRACES_SAMPLE_RATE = {
  dev: 0.6,
  staging: 0.1,
  prod: 0.1,
};

export const configureErrorTrackingService = (environment: Environment, dsn: string) => {
  Sentry.init({
    dsn: dsn,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    environment: environment,
    tracesSampleRate: ENV_TO_TRACES_SAMPLE_RATE?.[environment] ?? 0.1,
  });

  const errorTrackingService = {
    identifyUser: (user: Sentry.User | undefined) => Sentry.setUser(user || null),

    captureException: (params: unknown) => Sentry.captureException(params),
    captureUserFeedback: (params: { name: string; email: string; message: string }) => Sentry.captureFeedback(params),
  };

  return errorTrackingService;
};

export * from "./AppError";
export * from "./ErrorSeverity";
export * from "./Exception";
export * from "./ForbiddenException";
export * from "./InternalServerErrorException";
export * from "./InvalidComponentBlockException";
export * from "./NotAcceptableException";
export * from "./NotFoundException";
export * from "./UnauthorizedException";
export * from "./UnprocessableErrorException";
export * from "./WebsocketErrorException";
export * from "./unrecoverableError";
export * from "./useHandleException";
